/**
 * Scale degree recognition — "After this cadence, what scale degree did you hear?"
 *
 * The user picks from solfège syllables. Distractors are other scale degrees
 * from the same scale, weighted toward neighboring degrees (more confusable).
 */

import { AudioPlanBuilder, type AudioPlan } from "../audio-plan.js";
import type { Exercise, AnswerOption } from "../exercise.js";
import { Rng } from "../exercise.js";
import {
  appendCadence,
  pickKey,
  degreeOffset,
  MODE_PATTERNS,
  normalizeMode,
  type CadenceOptions,
  type KeyChoice,
} from "../utils.js";

const MAJOR_SOLFEGE = ["do", "re", "mi", "fa", "sol", "la", "ti"];
const MINOR_SOLFEGE = ["do", "re", "me", "fa", "sol", "le", "te"];

/** Chromatic syllable for an altered major-scale degree, keyed `${alt}${degree}`. */
const CHROMATIC_SOLFEGE: Record<string, string> = {
  "♯1": "di",
  "♯2": "ri",
  "♯4": "fi",
  "♭6": "le",
  "♭7": "te",
};

export type Alteration = "♯" | "♭";

export interface ScaleDegreeParams {
  degree: number;        // 1..7
  mode: "major" | "minor";
  /**
   * Chromatic alteration of `degree` in a major-key context (e.g. ♯4 → fi).
   * When set, `mode` is ignored and the exercise becomes a chromatic-degree
   * discrimination: the altered note against its two diatonic neighbours.
   */
  alteration?: Alteration;
}

export function generateScaleDegree(
  itemId: string,
  params: ScaleDegreeParams,
  seed: number,
  cadenceOpts?: CadenceOptions,
): Exercise {
  if (params.alteration) {
    return generateChromaticDegree(itemId, params.degree, params.alteration, seed, cadenceOpts);
  }
  const rng = new Rng(seed);
  const key = pickKey(rng, params.mode);
  const syllables = params.mode === "major" ? MAJOR_SOLFEGE : MINOR_SOLFEGE;
  const targetSyllable = syllables[params.degree - 1]!;

  // Build audio: cadence, then the target note an octave above tonic.
  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  const targetMidi = key.tonicMidi + 12 + degreeOffset(params.degree, params.mode);
  builder.addNote(targetMidi, 1.4, 0.22, "target");
  builder.advance(1.5);
  const audio = builder.build();

  // Build options: all 7 syllables, with the correct one included.
  // For a fully-loaded button bar, we present all 7 (this matches the demo UX).
  const options: AnswerOption[] = syllables.map((syl, i) => ({
    id: `degree-${i + 1}`,
    label: syl,
  }));

  return {
    itemId,
    exerciseType: "scale-degree-recognition",
    audio,
    prompt: "What scale degree did you hear?",
    options,
    correctAnswerId: `degree-${params.degree}`,
    meta: {
      keyName: key.name,
      seed,
      explanation: `Scale degree ${params.degree} (${targetSyllable}) in ${key.name}.`,
      distractorCount: 6,
      teachAudio: buildResolutionPlan(key, params.degree),
      tonicMidi: key.tonicMidi,
      tonalityMode: params.mode,
    },
  };
}

/**
 * Chromatic-degree recognition — a cadence sets a major key, then a single
 * chromatically altered degree plays. The learner discriminates it from its
 * two diatonic neighbours (e.g. fi heard against fa and sol).
 *
 * Distractors are deliberately the flanking diatonic degrees rather than other
 * chromatics: the ear skill is "did that note land on the natural degree or its
 * alteration?", which is how chromatic tones actually surface in tonal melody.
 */
function generateChromaticDegree(
  itemId: string,
  degree: number,
  alteration: Alteration,
  seed: number,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const rng = new Rng(seed);
  const key = pickKey(rng, "major");
  const chromaticSyllable = CHROMATIC_SOLFEGE[`${alteration}${degree}`] ?? `${alteration}${degree}`;

  // The diatonic degrees the chromatic sits between: sharps lean up toward the
  // next degree, flats lean down from the current one.
  const lowerDegree = alteration === "♯" ? degree : degree - 1;
  const upperDegree = alteration === "♯" ? degree + 1 : degree;
  const lowerSyllable = MAJOR_SOLFEGE[lowerDegree - 1]!;
  const upperSyllable = MAJOR_SOLFEGE[upperDegree - 1]!;

  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  const targetMidi =
    key.tonicMidi + 12 + degreeOffset(degree, "major") + (alteration === "♯" ? 1 : -1);
  builder.addNote(targetMidi, 1.4, 0.22, "target");
  builder.advance(1.5);
  const audio = builder.build();

  const correctId = `syl-${chromaticSyllable}`;
  const options: AnswerOption[] = rng.shuffle([
    { id: `syl-${lowerSyllable}`, label: lowerSyllable },
    { id: correctId, label: chromaticSyllable },
    { id: `syl-${upperSyllable}`, label: upperSyllable },
  ]);

  return {
    itemId,
    exerciseType: "scale-degree-recognition",
    audio,
    prompt: "What scale degree did you hear?",
    options,
    correctAnswerId: correctId,
    meta: {
      keyName: key.name,
      seed,
      explanation: `Chromatic degree ${chromaticSyllable} (${alteration}${degree}) in ${key.name}.`,
      distractorCount: options.length - 1,
      tonicMidi: key.tonicMidi,
      tonalityMode: "major",
    },
  };
}

/**
 * Build a stepwise scale walk from the target note to the closest tonic.
 * Played after a wrong answer so the user can hear how the degree resolves.
 *
 * Direction is chosen by proximity: degrees with offsets ≤ 6 walk DOWN to
 * the upper tonic (tonicMidi + 12); offsets > 6 walk UP to the next-octave
 * tonic. This minimizes the number of intervening steps and matches the
 * standard ear-training cue of "sing back to do via the closest path."
 *
 * Returns undefined for degree 1, which is already at the tonic and has
 * nothing to resolve.
 */
export function buildResolutionPlan(
  key: KeyChoice,
  degree: number,
  pattern?: readonly number[],
  octaveShift = 0,
): AudioPlan | undefined {
  if (degree === 1) return undefined;

  const resolvePattern = pattern ?? MODE_PATTERNS[normalizeMode(key.mode)];
  const targetOffset = resolvePattern[degree - 1]!;

  const offsets: number[] = [];
  if (targetOffset > 6) {
    for (let i = degree - 1; i < resolvePattern.length; i++) offsets.push(resolvePattern[i]!);
    offsets.push(12);
  } else {
    for (let i = degree - 1; i >= 0; i--) offsets.push(resolvePattern[i]!);
  }

  const builder = new AudioPlanBuilder();
  builder.advance(0.15);
  const noteDur = 0.4;
  const noteGap = 0.05;
  for (const off of offsets) {
    builder.addNote(key.tonicMidi + 12 + octaveShift + off, noteDur, 0.22, "melody");
    builder.advance(noteDur + noteGap);
  }
  builder.advance(0.3);
  return builder.build();
}
